import { DateTime } from "luxon";
import { getDates } from "../Alert/Filter/CreateFilterDateForm";
import { FilterType } from "../Alert/Filter/FilterType";
import { Operator } from "../constants";
import {
  DateOptions,
  DateRangeOptions,
  DimensionOptions,
  Filter,
  KpiOptions,
  StatusOptions,
} from "./Alert";

// `dimension_attribut` id of the alerts-page "Hôtel" dimension (standard seed,
// data/dimensions-attributes-translations.csv), a DIFFERENT id space from the
// helicopter filters (where property is 1).
export const ALERT_PAGE_PROPERTY_DIMENSION_ID = 4;
// Same seed: the "Uh type Nandc" dimension, valued by uh_type_nandc ids.
export const ALERT_PAGE_UH_TYPE_DIMENSION_ID = 26;

export enum Granularity {
  HEBERGEMENT = 0,
  UHTYPE = 1,
  UHCAT = 2,
}

// TODO: replace this with the enum
export type GranularityType = 0 | 1 | 2;

export interface FilterScope {
  startDate: string | null;
  endDate: string | null;
  granularity: GranularityType;
  dimensionFilters: Array<{
    id: number;
    values:
      | Array<string>
      | { startDate: string | null; endDate: string | null };
  }>;
  kpiFiltersHeb: Array<{ id: number; operator: Operator; value: number }>;
  kpiFiltersType: Array<{ id: number; operator: Operator; value: number }>;
  kpiFiltersCat: Array<{ id: number; operator: Operator; value: number }>;
}

export const buildDimensionFilters = (
  dimensions: Array<Filter>,
  granularityValue: GranularityType
) => {
  const scope: FilterScope = {
    startDate: null,
    endDate: null,
    granularity: granularityValue,
    dimensionFilters: [],
    kpiFiltersHeb: [],
    kpiFiltersType: [],
    kpiFiltersCat: [],
  };

  dimensions.forEach((filter) => {
    if (filter.type === FilterType.DATE) {
      const options = filter.options as DateOptions;

      const { start, end } = getDates(options);
      if (start.isValid && end.isValid) {
        scope.startDate = start.toISODate();
        scope.endDate = end.toISODate();
      } else {
        scope.startDate = DateTime.now().toISODate();
        scope.endDate = DateTime.now().toISODate();
      }
    } else if (filter.type === FilterType.MULTIPLE) {
      const options = filter.options as DimensionOptions;
      const data = {
        id: filter.target as number,
        values: options.values.map((value) =>
          // (id dimension 35 = "Status de prix")
          // (ids 41/42 = uh cat reference booleans — send "true"/"false" strings
          //  rather than the hardcoded frontend option id)
          // When these filters are selected, translate labels so we need
          // to use name param value to send to the backend instead of translated label
          filter.target === 35 || filter.target === 41 || filter.target === 42
            ? value.name
            : value.id.toString()
        ),
      };
      scope.dimensionFilters.push(data);
    } else if (filter.type === FilterType.STATUS) {
      const options = filter.options as StatusOptions;
      const data = {
        id: filter.target as number,
        values: [options.value],
      };
      scope.dimensionFilters.push(data);
    } else if (filter.type === FilterType.DATE_RANGE) {
      const options = filter.options as DateRangeOptions;
      const data = {
        id: filter.target as number,
        values: {
          startDate: options.values[0] || null,
          endDate: options.values[1] || null,
        },
      };
      scope.dimensionFilters.push(data);
    }
  });

  return scope;
};

export type AlertLevel = "property" | "uh_type_nandc" | "uh_cat";

export const ALERT_LEVELS: Record<GranularityType, AlertLevel> = {
  [Granularity.HEBERGEMENT]: "property",
  [Granularity.UHTYPE]: "uh_type_nandc",
  [Granularity.UHCAT]: "uh_cat",
};

export interface AlertOrdering {
  /** `date`, `property`, `uh_type_nandc`, `uh_cat`, `event` or a KPI code. */
  field: string;
  direction: "asc" | "desc";
}

/** The API's own default. */
export const DATE_ORDERING: AlertOrdering = { field: "date", direction: "asc" };

export interface AlertCountRequest {
  level: AlertLevel;
  start_date: string;
  end_date: string;
  dimension_filters: Array<{ dimension: string; values: string[] }>;
  kpi_filters: Array<{
    kpi: string;
    level: AlertLevel;
    operator: Operator;
    value: number;
  }>;
}

export interface AlertListRequest extends AlertCountRequest {
  kpis: string[];
  ordering?: AlertOrdering;
  page: number;
  page_size: number;
}

export interface AlertExportRequest extends AlertCountRequest {
  kpis: string[];
  ordering: AlertOrdering;
  /** Header per column, keyed by field or KPI code. */
  column_labels?: Record<string, string>;
}

export interface AlertDimension {
  name: string;
  granularity: number;
}

/**
 * The page keeps KPIs and dimensions by id (saved filter groups, URLs); the
 * API names them by KPI code and dimension name.
 */
export interface AlertCatalog {
  kpiCodes: Map<number, string>;
  dimensions: Map<number, AlertDimension>;
}

const lookup = <T,>(map: Map<number, T>, id: number, kind: string): T => {
  const value = map.get(id);
  if (value === undefined) {
    throw new Error(`The alert list does not support the ${kind} ${id}.`);
  }
  return value;
};

export const kpiCode = (catalog: AlertCatalog, kpiId: number) =>
  lookup(catalog.kpiCodes, kpiId, "KPI");

// Weekday values are sent by their English name ("Monday"), every other
// dimension by the value id.
const WEEKDAY_DIMENSION_ID = 29;

export const buildAlertCountRequest = (
  filters: { dimensions: Array<Filter>; kpis: Array<Filter> },
  catalog: AlertCatalog,
  granularity: GranularityType
): AlertCountRequest => {
  const today = DateTime.now();
  const request: AlertCountRequest = {
    level: ALERT_LEVELS[granularity],
    start_date: today.toISODate() ?? "",
    end_date: today.plus({ years: 1 }).toISODate() ?? "",
    dimension_filters: [],
    kpi_filters: [],
  };

  filters.dimensions.forEach((filter) => {
    if (filter.type === FilterType.DATE) {
      const { start, end } = getDates(filter.options as DateOptions);
      if (start.isValid && end.isValid) {
        request.start_date = start.toISODate() ?? request.start_date;
        request.end_date = end.toISODate() ?? request.end_date;
      } else {
        request.start_date = request.end_date = today.toISODate() ?? "";
      }
    } else if (filter.type === FilterType.MULTIPLE) {
      const target = filter.target as number;
      const dimension = lookup(catalog.dimensions, target, "dimension");
      const options = filter.options as DimensionOptions;
      // A filter on a finer level stays on the page while reading a coarser one.
      if (dimension.granularity <= granularity) {
        request.dimension_filters.push({
          dimension: dimension.name,
          values: options.values.map((value) =>
            target === WEEKDAY_DIMENSION_ID ? value.name : value.id.toString()
          ),
        });
      }
    } else {
      throw new Error(
        `The alert list does not support ${filter.type} filters.`
      );
    }
  });

  filters.kpis.forEach((filter) => {
    if (filter.type !== FilterType.KPI) {
      throw new Error(
        `The alert list does not support ${filter.type} filters.`
      );
    }
    const options = filter.options as KpiOptions;
    const kpiGranularity = options.granularity ?? Granularity.HEBERGEMENT;
    if (kpiGranularity <= granularity) {
      request.kpi_filters.push({
        kpi: kpiCode(catalog, options.kpi),
        level: ALERT_LEVELS[kpiGranularity],
        operator: options.operator,
        value: options.value,
      });
    }
  });

  return request;
};
